import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { publicApi } from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { CheckCircle, Download, Zap, Calendar, Users, Hash, Sparkles, LogIn } from 'lucide-react';
import { format } from 'date-fns';

export default function SuccessPage() {
  const [searchParams] = useSearchParams();
  const ref = searchParams.get('ref');
  const isFree = searchParams.get('free') === 'true';
  const isNewMember = searchParams.get('new_member') === '1';
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (ref) {
      publicApi.getCheckInByRef(ref)
        .then(res => setData(res.data))
        .catch(() => {});
    }
  }, [ref]);

  const checkin = data?.checkin;
  const payment = data?.payment;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0A0A0A' }}>
      <div className="max-w-[440px] mx-auto px-4 pt-12 pb-8">
        {/* Brand */}
        <div className="flex items-center gap-2 mb-8">
          <div className="w-7 h-7 rounded bg-gradient-to-br from-[#D4A843] to-[#B8923A] flex items-center justify-center">
            <Zap size={13} className="text-[#120F08]" />
          </div>
          <span className="font-['EB_Garamond'] text-[#F2F2F2] text-lg font-semibold">Scratch Unleashed</span>
        </div>

        {/* Success Icon */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full border-2 border-[#D4A843] flex items-center justify-center mx-auto mb-4 animate-pulse-gold">
            <CheckCircle size={32} className="text-[#D4A843]" />
          </div>
          <h1 className="font-['EB_Garamond'] text-3xl text-[#F2F2F2] mb-1">
            {isFree ? 'Check-in Confirmed!' : 'Payment Successful!'}
          </h1>
          <p className="text-[#B9B9B9] text-sm">
            {isFree ? "You're all set. See you tonight!" : 'Your cover charge has been received.'}
          </p>
        </div>

        {/* Receipt Card */}
        <div className="surface-card card-highlight rounded-xl p-5 space-y-3 mb-4" data-testid="payment-result-status-text">
          <div className="flex items-center justify-between text-sm">
            <span className="text-[#B9B9B9] flex items-center gap-1.5"><Hash size={11} /> Booking Ref</span>
            <span className="text-mono text-[#D4A843]">{ref || checkin?.reference_id || '—'}</span>
          </div>
          {checkin && (
            <>
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#B9B9B9] flex items-center gap-1.5"><Calendar size={11} /> Visit Date</span>
                <span className="text-[#F2F2F2]">
                  {checkin.visit_date ? (() => { try { return format(new Date(checkin.visit_date + 'T12:00:00'), 'EEE, dd MMM yyyy'); } catch { return checkin.visit_date; } })() : '—'}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#B9B9B9] flex items-center gap-1.5"><Users size={11} /> Guests</span>
                <span className="text-[#F2F2F2]">{checkin.guests}</span>
              </div>
              <div className="border-t border-[#2A2A2A] pt-3 flex items-center justify-between">
                <span className="text-[#F2F2F2] font-semibold">Amount Paid</span>
                <span className="font-['EB_Garamond'] text-2xl text-[#D4A843]">
                  {isFree ? 'FREE' : formatCurrency(payment?.amount || checkin.total_amount)}
                </span>
              </div>
            </>
          )}
          {payment?.transaction_id && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-[#6F6F6F]">Transaction ID</span>
              <span className="text-mono text-[#6F6F6F]">{payment.transaction_id}</span>
            </div>
          )}
        </div>

        <div className="bg-[#0E1F12] border border-[#1A3A24] rounded-xl p-4 mb-6">
          <p className="text-[#A8EFC0] text-sm text-center">Present this booking reference at the entrance</p>
        </div>

        {/* New Scratch Member Banner — shown only when an account was auto-created on this check-in */}
        {isNewMember && (
          <div
            data-testid="new-member-banner"
            className="rounded-xl p-5 mb-6 relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(212,168,67,0.12) 0%, rgba(212,168,67,0.04) 100%)',
              border: '1px solid rgba(212,168,67,0.4)'
            }}
          >
            <div className="flex items-start gap-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(212,168,67,0.18)' }}
              >
                <Sparkles size={18} className="text-[#D4A843]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-['EB_Garamond'] text-lg text-[#F2F2F2] font-semibold mb-1">
                  🎉 You're a Scratch Member!
                </h3>
                <p className="text-[#B9B9B9] text-xs leading-relaxed mb-3">
                  We've created your member account using this phone number. Login anytime from the homepage to view your bookings, set a password, and add your email.
                </p>
                <Button
                  onClick={() => navigate('/')}
                  data-testid="new-member-login-cta"
                  variant="outline"
                  size="sm"
                  className="bg-transparent border-[#D4A843]/40 text-[#D4A843] hover:bg-[#D4A843]/10 hover:border-[#D4A843] rounded-lg h-8 text-xs"
                >
                  <LogIn size={12} className="mr-1.5" />
                  Login from Homepage
                </Button>
              </div>
            </div>
          </div>
        )}

        <Button
          onClick={() => navigate('/checkin')}
          className="w-full btn-gold rounded-xl h-11 font-semibold"
        >
          New Check-in
        </Button>
      </div>
    </div>
  );
}
